import { useEffect, useMemo } from 'react';

import { useLiveQuery } from 'dexie-react-hooks';

import db, { DEXIE_IS_LOADING } from '../lib/db';
import { normalizeWidgetRuntimeTitle, storeWidgetRuntimeTitle } from '../lib/widgetRuntimeTitles';

const EMPTY_ACTIONS = [];
const runtimeTitleSubscriptions = new Map();

function isEligibleWidget(widget) {
  if (!widget || widget.status !== 'published') return false;
  if (widget.template_runtime_state && widget.template_runtime_state !== 'published') return false;
  if (!widget.url) return false;
  return Array.isArray(widget.message_actions) && widget.message_actions.length > 0;
}

function displayTitleFor(entry) {
  return (
    normalizeWidgetRuntimeTitle(entry.runtimeTitle?.title) ||
    normalizeWidgetRuntimeTitle(entry.widget.title) ||
    normalizeWidgetRuntimeTitle(entry.widget.default_title)
  );
}

function actionSortKey(entry) {
  return [displayTitleFor(entry), entry.action.label || '', entry.widget.id || '', entry.action.id || '']
    .map((part) => String(part).toLowerCase())
    .join('\n');
}

function displayLabelFor(entry, labelCounts) {
  const label = entry.action.label || 'Send to widget';
  return labelCounts.get(label) > 1 ? `${label} - ${displayTitleFor(entry)}` : label;
}

function widgetRuntimeWebSocketUrl(widget) {
  try {
    const url = new URL(widget.url);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    url.pathname = '/ws';
    url.search = '';
    url.hash = '';

    const defaultTitle = normalizeWidgetRuntimeTitle(widget.default_title || widget.title);
    if (defaultTitle) url.searchParams.set('default_title', defaultTitle);

    return url.toString();
  } catch {
    return null;
  }
}

function runtimeTitleSubscriptionKey(widget, url) {
  return [widget.workspace_id || '', widget.id || '', url].join('\n');
}

function acquireWidgetRuntimeTitleSubscription(widget) {
  const url = widgetRuntimeWebSocketUrl(widget);
  if (!url) return null;

  const key = runtimeTitleSubscriptionKey(widget, url);
  const existingSubscription = runtimeTitleSubscriptions.get(key);
  if (existingSubscription) {
    existingSubscription.refCount += 1;
    return () => releaseWidgetRuntimeTitleSubscription(key);
  }

  const subscription = { key, refCount: 1, socket: null, widget };
  runtimeTitleSubscriptions.set(key, subscription);
  openWidgetRuntimeTitleSocket(subscription);

  return () => releaseWidgetRuntimeTitleSubscription(key);
}

function openWidgetRuntimeTitleSocket(subscription) {
  const url = widgetRuntimeWebSocketUrl(subscription.widget);
  if (!url || runtimeTitleSubscriptions.get(subscription.key) !== subscription || subscription.refCount <= 0) return;

  let socket;
  try {
    socket = new WebSocket(url);
  } catch {
    if (runtimeTitleSubscriptions.get(subscription.key) === subscription && subscription.refCount > 0) {
      window.setTimeout(() => openWidgetRuntimeTitleSocket(subscription), 1000);
    }
    return;
  }

  subscription.socket = socket;
  socket.addEventListener('message', (event) => {
    try {
      const message = JSON.parse(event.data);
      const title = message?.type === 'widget_title' ? message.title?.title : null;
      storeWidgetRuntimeTitle({
        workspaceId: subscription.widget.workspace_id,
        widgetId: subscription.widget.id,
        title,
      }).catch(() => {});
    } catch {
      // Ignore malformed widget runtime messages.
    }
  });
  socket.addEventListener('close', () => {
    if (runtimeTitleSubscriptions.get(subscription.key) !== subscription) return;

    subscription.socket = null;
    if (subscription.refCount > 0) window.setTimeout(() => openWidgetRuntimeTitleSocket(subscription), 1000);
  });
}

function releaseWidgetRuntimeTitleSubscription(key) {
  const subscription = runtimeTitleSubscriptions.get(key);
  if (!subscription) return;

  subscription.refCount -= 1;
  if (subscription.refCount > 0) return;

  runtimeTitleSubscriptions.delete(key);
  subscription.socket?.close();
}

export function useMessageWidgetActions(workspaceId, { enabled = true } = {}) {
  const widgets = useLiveQuery(
    () => {
      if (!workspaceId || !enabled) return [];
      return db.widgets.where('[workspace_id+status]').equals([workspaceId, 'published']).toArray();
    },
    [enabled, workspaceId],
    DEXIE_IS_LOADING,
  );

  const runtimeTitles = useLiveQuery(
    () => {
      if (!workspaceId || !enabled) return [];
      return db.widget_runtime_titles.where('workspace_id').equals(workspaceId).toArray();
    },
    [enabled, workspaceId],
    DEXIE_IS_LOADING,
  );

  useEffect(() => {
    if (!enabled || widgets === DEXIE_IS_LOADING) return undefined;

    const unsubscribe = (widgets || [])
      .filter(isEligibleWidget)
      .map((widget) => acquireWidgetRuntimeTitleSubscription(widget))
      .filter(Boolean);

    return () => {
      unsubscribe.forEach((release) => release());
    };
  }, [enabled, widgets]);

  return useMemo(() => {
    if (!enabled || widgets === DEXIE_IS_LOADING || runtimeTitles === DEXIE_IS_LOADING) return EMPTY_ACTIONS;

    const runtimeTitlesByWidgetId = new Map((runtimeTitles || []).map((runtimeTitle) => [runtimeTitle.widget_id, runtimeTitle]));

    const entries = (widgets || [])
      .filter(isEligibleWidget)
      .flatMap((widget) =>
        widget.message_actions.map((action) => ({
          key: `${widget.id}:${action.id}`,
          runtimeTitle: runtimeTitlesByWidgetId.get(widget.id) || null,
          widget,
          action,
        })),
      )
      .sort((a, b) => actionSortKey(a).localeCompare(actionSortKey(b)));

    const labelCounts = entries.reduce((counts, entry) => {
      const label = entry.action.label || 'Send to widget';
      counts.set(label, (counts.get(label) || 0) + 1);
      return counts;
    }, new Map());

    return entries.map((entry) => ({
      ...entry,
      widgetId: entry.widget.id,
      actionId: entry.action.id,
      label: displayLabelFor(entry, labelCounts),
      iconUrl: entry.action.icon_url || null,
    }));
  }, [enabled, runtimeTitles, widgets]);
}
